import Icon from '@components/global/new/Icon/Icon';
import Status from '@components/global/new/Status/Status';
import { MODALS_ENUM } from '@constants/Modals';
import { IEquipmentModal3Props } from '@mobx/components/EquipmentModal3';
import { MinerControlRequestTyped } from '@services/minerContol';
import useTypedRequest from '@utils/hooks/useTypedRequest';
import cn from "classnames";
import React, { CSSProperties, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import Switch from 'react-switch';
import { Tooltip } from 'antd';
import { Handle, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import { show } from 'redux-modal';
import { IS_DEBUG, QUICK_DEBUG } from '../../../config';
import NodeIcon3 from './NodeIcon';
import styles from './nodes.module.scss';

import cellSVG from "@assets/icons/cell.svg";
import consumerSVG from "@assets/icons/consumer.svg";
import counterSVG from "@assets/icons/counter.svg";
import copyIcon from '@assets/icons/copy.svg';
import networkSVG from "@assets/icons/network.svg";
import closeIcon from '@assets/icons/node_close.svg';
import editIcon from '@assets/icons/node_edit.svg';
import resetIcon from '@assets/icons/node_reset.svg';
import startIcon from '@assets/icons/node_start.svg';
import stopIcon from '@assets/icons/node_stop.svg';
import shieldSVG from "@assets/icons/shield.svg";
import transformerSVG from "@assets/icons/transformer.svg";
import { ConfirmModalExposedPropsI } from '@components/modals/newModals/ConfirmModal/ConfirmModal';
import { useEditorState3 } from '@mobx/hooks/useEditorState3';
import connectionsStore, { getNodeTypeColor3, isRackSectionData3, isStateOn3, NodeData3, NodeFigure3, NodeSignal3, NodeState3, NodeType3, toWorkEquipment3 } from '@mobx/store/connections';
import { EQUIPMENT_TYPE } from '@store/Equipments/actionTypes';
import { observer } from 'mobx-react-lite';


export interface NodeTypeDescription3 {
  type: NodeType3;
  icon: string;
  color: string;
  input: boolean;
  output: boolean;
  kind?: EQUIPMENT_TYPE;
}

export const nodesDescriptions3: NodeTypeDescription3[] = [
  { type: "cell",        color: getNodeTypeColor3("cell"),        icon: cellSVG,        input: false, output: true, kind: EQUIPMENT_TYPE.ELECTRICAL },
  { type: "transformer", color: getNodeTypeColor3("transformer"), icon: transformerSVG, input: true,  output: true, kind: EQUIPMENT_TYPE.ELECTRICAL },
  { type: "counter",     color: getNodeTypeColor3("counter"),     icon: counterSVG,     input: true,  output: true, kind: EQUIPMENT_TYPE.ELECTRICAL },
  { type: "shield",      color: getNodeTypeColor3("shield"),      icon: shieldSVG,      input: true,  output: true, kind: EQUIPMENT_TYPE.ELECTRICAL },
  //{ type: "building",    color: "#E8F3E2", icon: buildingSVG,    input: true,  output: true, kind: EQUIPMENT_TYPE.ELECTRICAL },

  { type: "consumer", color: getNodeTypeColor3("consumer"), icon: consumerSVG,    input: true,  output: false },

  { type: "square",   color: getNodeTypeColor3("square"),   icon: networkSVG, input: true,  output: true, kind: EQUIPMENT_TYPE.NETWORK },
  { type: "triangle", color: getNodeTypeColor3("triangle"), icon: networkSVG, input: true,  output: true, kind: EQUIPMENT_TYPE.NETWORK },
  { type: "circle",   color: getNodeTypeColor3("circle"),   icon: networkSVG, input: true,  output: true, kind: EQUIPMENT_TYPE.NETWORK },
  { type: "diamond",  color: getNodeTypeColor3("diamond"),  icon: networkSVG, input: true,  output: true, kind: EQUIPMENT_TYPE.NETWORK },
];

type NodesMap = {
  [k in NodeType3]: NodeTypeDescription3
}

type NodesCounter = {
  [k in NodeType3]: number
}

let nodesCounter: NodesCounter = {
  cell: 0,
  transformer: 0,
  counter: 0,
  shield: 0,
  consumer: 0,
  network: 0,
  square: 0,
  triangle: 0,
  circle: 0,
  diamond: 0,
  //building: 0,
}

export const incrementNodesCounter3 = (t: NodeType3) => nodesCounter[t]++;

export const nodesDescriptionMap3: NodesMap = Object.fromEntries(nodesDescriptions3.map(node => [node.type, node])) as NodesMap;
const handleStyle: CSSProperties = {width: "10px", height: "10px"};

interface CustomNodeComponentProps {
  descr: NodeTypeDescription3;
  className: string;
  data: NodeData3;
}

const SECOND = 1000;
const CONTROL_TIMEOUT_SECONDS = QUICK_DEBUG ? 5 : 60;
const CONTROL_TIMEOUT = CONTROL_TIMEOUT_SECONDS*SECOND; //1 минута

export const timePassedSinceControl3 = (last?: number) => {
  if (!last) return undefined;
  return (new Date).getTime() - last;
};

export const timeLeftUntilControl3 = (last?: number) => {
  const passed = timePassedSinceControl3(last);
  if (!passed) return 0;
  return CONTROL_TIMEOUT > passed ? CONTROL_TIMEOUT - passed : 0;
};

const hasSectionMiner = (data: NodeData3) =>
  data.consumer === "section" &&
  isRackSectionData3(data.userData) &&
  data.userData.miner !== undefined;

const isNodeNameEditable = (data: NodeData3) => data.consumer === undefined;

const useControl = (props: CustomNodeComponentProps) => {
  const inputEdges = useEditorState3(
    props.data.shemeKind, 
    props.data.placeID, 
    st => st?.data.edges.filter(edge => edge.target === `#${props.data.ID}`)
  ) ?? [];
  const locked = useEditorState3(
    props.data.shemeKind,
    props.data.placeID,
    st => st?.locked
  ) ?? false;
  const placeStore = useMemo(() => props.data.placeID ? (connectionsStore.getPlaceStore(props.data.placeID)) : undefined, [props.data.placeID]);

  const [isEditName, setIsEditName] = useState(false);
  const [hover, setHover] = useState(false);
  const nameElementRef = useRef<HTMLElement | null>(null);
  const [nameKey, setNameKey] = useState(0);
  const dispatch = useDispatch();
  const self = useRef<HTMLDivElement | null>(null);
  const {request: controlMiner, loading } = useTypedRequest(MinerControlRequestTyped);
  const [timeLeft, setTimeLeft] = useState(timeLeftUntilControl3(props.data.lastSignal?.ts));
  const [showTimer, setShowTimer] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval>>();
  const [waitingSwitch, setWaitingSwitch_] = useState<boolean>();
  const success = useMemo(() => props.data.lastSignal?.success, [props.data.lastSignal?.success]);

  const setWaitingSwitch: typeof setWaitingSwitch_ = (x) => {
    //dbg.trace("setWaitingSwitch", props.data.ID, x);
    return setWaitingSwitch_(x);
  }

  //useEffect(() => dbg.log("SUCCESS", props.data.ID, [success]), [success]);

  useEffect(() => {
    if (timer.current && !timeLeft) {
      clearInterval(timer.current);
      timer.current = undefined;
      setShowTimer(false);
    }
  }, [timeLeft]);

  useEffect(() => {
    if (waitingSwitch !== undefined) {
      const tm = setTimeout(() => setWaitingSwitch(undefined), 30*1000);
      return () => clearTimeout(tm);
    }
  }, [waitingSwitch]);

  useEffect(() => {
    if (success === false) {
      setWaitingSwitch(undefined);
    }
  }, [success]);

  const onMouseEnterControls = useCallback(() => {
    const left = timeLeftUntilControl3(props.data.lastSignal?.ts); 
    setTimeLeft(left);
    if (left && !timer.current) {
      timer.current = setInterval(() => {
        setTimeLeft(timeLeftUntilControl3(props.data.lastSignal?.ts));
      }, 1000);
      setShowTimer(true);
    }
  }, [props.data.lastSignal]);

  const onMouseLeaveControls = () => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = undefined;
      setShowTimer(false);
    }
  }

  const resetName = () => setNameKey(k => k + 1);

  const onEditNode = () => {
    dispatch(show(MODALS_ENUM.EQUIPMENT_MODAL, {
      workEq: toWorkEquipment3(props.data),
      placeID: props.data.placeID,
    } as IEquipmentModal3Props));
  };

  return {
    isInputBusy: inputEdges?.length > 0,
    isOutputBuzy: false,
    isEditName,
    nameElementRef,
    nameKey,
    self,
    hover,
    locked,
    loading,
    timeLeftUntilControl: timeLeft,
    showTimer: false, // отключил таймеры
    state: props.data.state,
    waitingSwitch,

    onEditNode,
    onMouseEnterControls,
    onMouseLeaveControls,
    removeNode: () => {
      const mprops: ConfirmModalExposedPropsI = {
        async onConfirm() {
          placeStore?.removeNode(props.data.ID);
        }
      }
      dispatch(show(MODALS_ENUM.CONFIRM_MODAL, mprops));
    },
    removeEdge: () => placeStore?.removeEdge(props.data.ID),
    setEditName(v: boolean) {
      if (!isNodeNameEditable(props.data)) return;
      if (nameElementRef.current) nameElementRef.current.focus();
      setIsEditName(v);
    },
    onNameEditorKeyDown(ev: KeyboardEvent<HTMLElement>) {
      const stop = (ev: KeyboardEvent<HTMLElement>) => {
        setIsEditName(false);
        ev.preventDefault();
        ev.stopPropagation();
        resetName();
      }
      if (ev.key === "Escape") {
        stop(ev);
        return;
      }
      if (ev.key !== "Enter") return;
      stop(ev);
      const val = (ev.target as HTMLElement).textContent;
      placeStore?.setNodeName(props.data.ID, val || "");
    },
    async restart(data: unknown) {
      placeStore?.sendSignal(NodeSignal3.RESET, `#${props.data.ID}`);
    },
    startNode() { placeStore?.sendSignal(NodeSignal3.START, `#${props.data.ID}`) },
    stopNode() { placeStore?.sendSignal(NodeSignal3.STOP, `#${props.data.ID}`) },
    switchMiner() {
      const signal = isStateOn3(props.data.state) ? NodeSignal3.STOP : NodeSignal3.START;
      placeStore?.sendSignal(signal, `#${props.data.ID}`);
      setWaitingSwitch(signal === NodeSignal3.START);
    },
  }
}

const ComplexState = (props: {on: number, off: number}) => (
  <div className={styles.complexState}>
    <span>{props.on}</span>
    /
    <span>{props.off}</span>
  </div>
)

const shouldRenderNodeState = (data: NodeData3) =>
  !(data.consumer === "section" && !hasSectionMiner(data));

const getConsumerDisplayName = (data: NodeData3) => {
  if (data.consumer === "section" && isRackSectionData3(data.userData) && data.userData.miner) {
    return data.userData.miner.miner_name;
  }

  return data.name;
};

const stopFlowPan = (ev: React.SyntheticEvent) => {
  ev.stopPropagation();
};

const clearNodeTextSelection = () => {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    selection.removeAllRanges();
  }

  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

type SelectionSnapshot = {
  hasSelection: boolean;
  text: string;
  anchorNode: Node | null;
  anchorOffset: number;
  focusNode: Node | null;
  focusOffset: number;
};

const getSelectionSnapshot = (): SelectionSnapshot => {
  const selection = window.getSelection();
  return {
    hasSelection: !!selection && selection.rangeCount > 0 && selection.toString().length > 0,
    text: selection?.toString() ?? "",
    anchorNode: selection?.anchorNode ?? null,
    anchorOffset: selection?.anchorOffset ?? 0,
    focusNode: selection?.focusNode ?? null,
    focusOffset: selection?.focusOffset ?? 0,
  };
};

const isSameSelectionSnapshot = (a: SelectionSnapshot, b: SelectionSnapshot) =>
  a.hasSelection === b.hasSelection &&
  a.text === b.text &&
  a.anchorNode === b.anchorNode &&
  a.anchorOffset === b.anchorOffset &&
  a.focusNode === b.focusNode &&
  a.focusOffset === b.focusOffset;

const useCtrlTextSelectMode = (locked: boolean) => {
  const [ctrlPressed, setCtrlPressed] = useState(false);

  useEffect(() => {
    if (locked) {
      setCtrlPressed(false);
      return;
    }

    const syncCtrlState = (ev: globalThis.KeyboardEvent) => {
      setCtrlPressed(ev.ctrlKey);
    };

    const clearCtrlState = () => setCtrlPressed(false);

    document.addEventListener("keydown", syncCtrlState);
    document.addEventListener("keyup", syncCtrlState);
    window.addEventListener("blur", clearCtrlState);

    return () => {
      document.removeEventListener("keydown", syncCtrlState);
      document.removeEventListener("keyup", syncCtrlState);
      window.removeEventListener("blur", clearCtrlState);
    };
  }, [locked]);

  return !locked && ctrlPressed;
};

const NodeName = (props: {
  name: string,
  data: NodeData3,
  ctrl: ReturnType<typeof useControl>,
  ctrlTextSelectMode: boolean,
}) => {
  const { formatMessage } = useIntl();
  const textSelectClassName = props.ctrlTextSelectMode
    ? cn("nopan", "nodrag", styles.textSelectMode)
    : undefined;

  const onCopyName = (ev: React.SyntheticEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    navigator.clipboard?.writeText(props.name).catch(() => undefined);
  };

  return <div className={cn(styles.nodeName, textSelectClassName)}>
    <div className={cn(styles.titleRow, textSelectClassName)}>
      <span 
        ref={props.ctrl.nameElementRef} 
        contentEditable={isNodeNameEditable(props.data) && props.ctrl.isEditName}
        onClick={() =>
          !props.ctrlTextSelectMode &&
          isNodeNameEditable(props.data) &&
          props.ctrl.setEditName(true)
        }
        onKeyDown={props.ctrl.onNameEditorKeyDown}
        key={props.ctrl.nameKey}
        className={cn(styles.title, textSelectClassName)}
      >
        {props.name}
      </span>
      <Tooltip title={formatMessage({ id: "machines.table.tabs.copy" })} destroyTooltipOnHide>
        <span
          className={cn("nopan", "nodrag", styles.copyNameButton)}
          onPointerDown={stopFlowPan}
          onMouseDown={stopFlowPan}
          onClick={onCopyName}
        >
          <Icon src={copyIcon} className={styles.copyNameIcon}/>
        </span>
      </Tooltip>
    </div>
    {shouldRenderNodeState(props.data) && (
      <div className={textSelectClassName}>
        { typeof props.data.state === "object"
          ? <ComplexState on={props.data.state.on} off={props.data.state.off}/>
          : <Status status={props.data.state} crop/>
        }
      </div>
    )}
  </div>;
}

const SwitchReset = (props: {reset?: boolean, data: NodeData3, bgColor: string, ctrl: ReturnType<typeof useControl>}) => {
  const { formatMessage } = useIntl();
  const isSwitchChecked =
    props.ctrl.waitingSwitch === undefined
      ? isStateOn3(props.data.state)
      : props.ctrl.waitingSwitch;
  const switchTooltip = formatMessage({
    id: isSwitchChecked
      ? "location.diagram.node_action.turn_off"
      : "location.diagram.node_action.turn_on",
  });

  return <div 
    className={cn("nopan", "nodrag", styles.switch, {
      [styles.hasTimer]: !!props.ctrl.timeLeftUntilControl,
      [styles.reset]: !!props.reset,
    })} 
    onPointerDown={stopFlowPan}
    onMouseDown={stopFlowPan}
    onMouseEnter={props.ctrl.onMouseEnterControls}
    onMouseLeave={props.ctrl.onMouseLeaveControls}
  >
    { props.ctrl.showTimer && 
        <div className={styles.switchTimer} style={{backgroundColor: props.bgColor}} >
          {CONTROL_TIMEOUT_SECONDS - Math.floor(props.ctrl.timeLeftUntilControl/SECOND)}
        </div>
    }
    {!props.reset && 
      (props.data.consumer === "miner" || hasSectionMiner(props.data)
      ? <div 
          className={cn("nopan", "nodrag", styles.switchContainter, {[styles.disabled]: false/*isSwitchFrozen(data.on)*/})} 
          onPointerDown={stopFlowPan}
          onMouseDown={stopFlowPan}
          onClick={ props.ctrl.switchMiner }
        >
          <Tooltip title={switchTooltip} destroyTooltipOnHide>
            <span className={cn("nopan", "nodrag")}>
              <Switch 
                onChange={() => {}} 
                checked={isSwitchChecked} 
                height={15} 
                width={25}
                uncheckedIcon={false}
                checkedIcon={false}
              />
            </span>
          </Tooltip>
        </div>
      : props.data.consumer === "section"
      ? null
      : <div 
          className={cn("nopan", "nodrag", styles.switchContainter, {[styles.disabled]: false/*isSwitchFrozen(data.on)*/})} 
          onPointerDown={stopFlowPan}
          onMouseDown={stopFlowPan}
        >
          <Tooltip title={formatMessage({ id: "location.diagram.node_action.start" })} destroyTooltipOnHide>
            <Icon
              src={startIcon}
              className={cn("nopan", "nodrag", styles.closerImg)}
              onPointerDown={stopFlowPan}
              onMouseDown={stopFlowPan}
              onClick={props.ctrl.startNode}
            />
          </Tooltip>
          <Tooltip title={formatMessage({ id: "location.diagram.node_action.stop" })} destroyTooltipOnHide>
            <Icon
              src={stopIcon}
              className={cn("nopan", "nodrag", styles.closerImg)}
              onPointerDown={stopFlowPan}
              onMouseDown={stopFlowPan}
              onClick={props.ctrl.stopNode}
            />
          </Tooltip>
        </div>
      )
    }
    {!!props.reset && (props.data.consumer !== "section" || hasSectionMiner(props.data)) &&
      <Tooltip title={formatMessage({ id: "location.diagram.node_action.reset" })} destroyTooltipOnHide>
        <Icon
          src={resetIcon}
          className={cn("nopan", "nodrag", styles.closerImg)}
          onPointerDown={stopFlowPan}
          onMouseDown={stopFlowPan}
          onClick={() => !props.ctrl.timeLeftUntilControl && props.ctrl.restart(props.data.userData)}
        />
      </Tooltip>
    }
  </div>;
}

const CustomNodeComponent = observer((props: CustomNodeComponentProps) => {
  const { formatMessage } = useIntl();
  const data = props.data;
  const ctrl = useControl(props);
  const nodeInfo = props.data.info?.trim();
  const ctrlTextSelectMode = useCtrlTextSelectMode(ctrl.locked);
  const nodeSelectClassName = ctrlTextSelectMode
    ? cn("nopan", "nodrag", styles.textSelectMode)
    : undefined;
  const mouseDownSelectionRef = useRef<SelectionSnapshot | null>(null);

  //useEffect(() => dbg.log(`NODE ${data.ID} DATA:`, data), [data]);
  //useEffect(() => dbg.log(`NODE ${data.ID} STATE:`, data.state), [data.state]);

  const onNodeMouseDown = () => {
    mouseDownSelectionRef.current = getSelectionSnapshot();
  };

  const onNodeMouseUp = () => {
    const mouseDownSelection = mouseDownSelectionRef.current;
    mouseDownSelectionRef.current = null;
    if (ctrl.locked || ctrlTextSelectMode || !mouseDownSelection?.hasSelection) return;

    const mouseUpSelection = getSelectionSnapshot();
    if (!isSameSelectionSnapshot(mouseDownSelection, mouseUpSelection)) return;

    clearNodeTextSelection();
  };

  return (
    <Tooltip
      open={nodeInfo ? undefined : false}
      title={<p className={styles.nodeInfoTooltip}>{nodeInfo}</p>}
      destroyTooltipOnHide
    >
      <div className={styles.nodeBackground}>
        <div 
          className={cn(styles.nodeWrapper, nodeSelectClassName, {
            [styles.hover]: ctrl.hover,
            [styles.inactive]: ctrl.loading,
            [styles.highlighted]: props.data.highlighted,
          })} 
          style={{backgroundColor: props.data.type !== "consumer" && props.data.color ? props.data.color : props.descr.color}} 
          ref={ctrl.self}
          onMouseDown={onNodeMouseDown}
          onMouseUp={onNodeMouseUp}
      >
        {!ctrl.locked && (
          <div className={styles.closer}>
            {IS_DEBUG && <span style={{fontSize: "10px", marginRight: "5px"}}>#{props.data.ID}</span>}
            {props.data.type !== "consumer" && (
              <Tooltip title={formatMessage({ id: "location.diagram.node_action.edit" })} destroyTooltipOnHide>
                <Icon src={editIcon} className={styles.closerImg} onClick={ctrl.onEditNode}/>
              </Tooltip>
            )}
            <Tooltip title={formatMessage({ id: "location.diagram.node_action.delete" })} destroyTooltipOnHide>
              <Icon src={closeIcon} className={styles.closerImg} onClick={ctrl.removeNode}/>
            </Tooltip>
          </div>
        )}
        { props.descr.input && 
          <Handle
            type="target" 
            position={Position.Left} 
            style={handleStyle} 
            isConnectable={!ctrl.isInputBusy}
          />
        }
        { props.descr.input && ctrl.isInputBusy &&
          <div className={styles.handleCloser} onClick={ctrl.removeEdge}>
            <Icon src={closeIcon} className={styles.closerImg}/>
          </div>
        }
        <div className={styles.nodeIcon}>
          <NodeIcon3 
            icon={props.descr.icon} 
            size={30} 
            figure={props.data.kind === EQUIPMENT_TYPE.NETWORK ? props.data.type as NodeFigure3 : undefined}
            figureColor={props.data.kind === EQUIPMENT_TYPE.NETWORK ? props.descr.color : undefined}
            consumer={props.data.consumer}
          />
        </div>
        <div className={cn(styles.node, props.className)}>
          <NodeName
            name={getConsumerDisplayName(data)}
            data={data}
            ctrl={ctrl}
            ctrlTextSelectMode={ctrlTextSelectMode}
          />
          {props.data.shemeKind === EQUIPMENT_TYPE.ELECTRICAL && <>
            <SwitchReset ctrl={ctrl} data={data} bgColor={props.descr.color} reset/>
            <SwitchReset ctrl={ctrl} data={data} bgColor={props.descr.color}/>
          </>}
        </div>
        { props.descr.output && 
          <Handle 
            type="source" 
            position={Position.Right} 
            style={handleStyle} 
            isConnectable={!ctrl.isOutputBuzy}
          /> 
        }
      </div>
      </div>
    </Tooltip>
  )
});

CustomNodeComponent.displayName = "CustomNodeComponent";

export const CellNode3: React.FC<{ data: NodeData3 }> = ({ data }) => 
  <CustomNodeComponent descr={nodesDescriptionMap3.cell} className={styles.cell} data={data}/>;
export const TransformerNode3: React.FC<{ data: NodeData3 }> = ({ data }) => 
  <CustomNodeComponent descr={nodesDescriptionMap3.transformer} className={styles.transformer} data={data}/>;
export const CounterNode3: React.FC<{ data: NodeData3 }> = ({ data }) => 
  <CustomNodeComponent descr={nodesDescriptionMap3.counter} className={styles.counter} data={data}/>;
export const ShieldNode3: React.FC<{ data: NodeData3 }> = ({ data }) => 
  <CustomNodeComponent descr={nodesDescriptionMap3.shield} className={styles.shield} data={data}/>;

export const ConsumerNode3: React.FC<{ data: NodeData3 }> = ({ data }) => 
  <CustomNodeComponent descr={nodesDescriptionMap3.consumer} className={styles.consumer} data={data}/>;

export const SquareNode3: React.FC<{ data: NodeData3 }> = ({ data }) => 
  <CustomNodeComponent descr={nodesDescriptionMap3.square} className={styles.square} data={data}/>;
export const TriangleNode3: React.FC<{ data: NodeData3 }> = ({ data }) => 
  <CustomNodeComponent descr={nodesDescriptionMap3.triangle} className={styles.triangle} data={data}/>;
export const CircleNode3: React.FC<{ data: NodeData3 }> = ({ data }) => 
  <CustomNodeComponent descr={nodesDescriptionMap3.circle} className={styles.circle} data={data}/>;
export const DiamondNode3: React.FC<{ data: NodeData3 }> = ({ data }) => 
  <CustomNodeComponent descr={nodesDescriptionMap3.diamond} className={styles.diamond} data={data}/>;
